import { useAuth } from '../contexts/AuthContext'
import { useEffect } from 'react'
import { useNavigate, useLocation, Location } from 'react-router-dom'

// Session storage key for return URL (persists across OAuth redirect)
const RETURN_URL_KEY = 'fd_qa_tools_return_url'

export default function Login() {
  const { login, isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Get the intended destination from location state (set by ProtectedRoute)
  const from = (location.state as { from?: Location })?.from
  const returnPath = from ? `${from.pathname}${from.search}${from.hash}` : '/'

  // Store return URL in sessionStorage when we land on login page
  // This persists across the OAuth redirect to Cognito
  useEffect(() => {
    if (returnPath && returnPath !== '/') {
      sessionStorage.setItem(RETURN_URL_KEY, returnPath)
    }
  }, [returnPath])

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // Check for stored return URL first, then fall back to state, then home
      const storedReturnUrl = sessionStorage.getItem(RETURN_URL_KEY)
      const targetUrl = storedReturnUrl || returnPath

      // Clean up the stored URL
      sessionStorage.removeItem(RETURN_URL_KEY)

      navigate(targetUrl, { replace: true })
    }
  }, [isAuthenticated, isLoading, navigate, returnPath])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-linear-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-linear-border border-t-linear-accent rounded-full animate-spin"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-linear-bg flex items-center justify-center p-4">
      <div className="bg-linear-bg-card border border-linear-border-input rounded-xl p-8 max-w-sm w-full shadow-lg">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-10 h-10 bg-mustard rounded-lg flex items-center justify-center">
            <span className="text-[16px] font-semibold text-white">FD</span>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-[20px] font-medium text-linear-text mb-2">Five & Done QA Tools</h1>
          <p className="text-[13px] text-linear-text-muted">AI-powered testing workflows</p>
        </div>

        {/* Sign in button */}
        <button
          onClick={login}
          className="w-full flex items-center justify-center gap-3 px-4 py-2.5 bg-linear-bg-button border border-linear-border-input rounded-lg hover:bg-linear-bg-hover transition-colors text-[13px] font-medium text-linear-text shadow-[0px_1px_1px_rgba(0,0,0,0.15)]"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Sign in with Google
        </button>

        {/* Footer */}
        <p className="text-center text-[11px] text-linear-text-placeholder mt-6">
          Internal tool
        </p>
      </div>
    </div>
  )
}
